import React from 'react'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { getActiveSegmentsFromScore, getColorForEnergyMode } from '@vacationtracker/shared/functions/user-insights/energy-visualization'
import { EnergyIndicator } from '../EnergyIndicator'

/** The granularity `getActiveSegmentsFromScore` already maps scores onto for the Energy Level
 * page's gauge, so a factor cannot read one way here and another way there. */
const FACTOR_SEGMENTS = 12

export interface EnergyFactorRow {
  /** Already localized. The design system holds no copy of its own. */
  name: string
  /** 0-100. Drives both the segment count and the colour. */
  score: number
}

export interface EnergyDetailRow {
  label: string
  value: string
}

export interface EnergyDetailsCardProps {
  /** 0-100. The headline reading, and what the mode name is derived from. */
  score: number
  /** The mode's display name, already localized ("Struggling"). Passed rather than derived so the
   * card cannot disagree with the caller's own catalogue about what to call a mode. */
  modeName: string
  /** Rendered as "<score>/<max>" under the mode. */
  scoreMax?: number
  factors: EnergyFactorRow[]
  /** Label/value pairs so the card does not need to know which facts a caller has. */
  details?: EnergyDetailRow[]
  /** Accessible name for the battery in the header. */
  batteryLabel: string
}

/**
 * The full energy reading: the headline score and mode, every factor that fed it, and the facts
 * underneath, in a form small enough to hang off an avatar.
 *
 * Holds NO copy of its own - every string arrives localized, factor names included, because the
 * design system cannot own product copy without the two drifting. The factors carry no per-factor
 * explanation affordance: the card states the numbers, the Energy Level page teaches them.
 *
 * Dark on purpose: it is overlay furniture, so it does NOT follow the page's light/dark theme - an
 * overlay that inverts with the theme stops being recognisable as an overlay.
 */
export function EnergyDetailsCard({ score, modeName, scoreMax = 100, factors, details, batteryLabel }: EnergyDetailsCardProps) {
  const modeColor = getColorForEnergyMode(getEnergyMode(score > 1 ? score : score * 100))

  return (
    <div style={{ display: 'grid', gap: 10, minWidth: 216, color: '#f5f5f5' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <EnergyIndicator orientation="vertical" size="small" score={score} label={batteryLabel} />
        <div style={{ display: 'grid', gap: 1 }}>
          <span style={{ color: modeColor, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{modeName}</span>
          <span style={{ color: '#a6a6a6', fontSize: 12, lineHeight: 1.2 }}>
            {score}/{scoreMax}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 5 }}>
        {factors.map((factor) => (
          <div key={factor.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{factor.name}</span>
            <FactorMeter score={factor.score} />
          </div>
        ))}
      </div>

      {details && details.length > 0 && (
        <div style={{ display: 'grid', gap: 3, borderTop: '1px solid #434343', paddingTop: 8 }}>
          {details.map((row) => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
              <span style={{ color: '#a6a6a6' }}>{row.label}</span>
              <span style={{ fontWeight: 700 }}>{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Segments rather than a continuous bar for the same reason the battery has bars: a countable
 * quantity survives at small sizes, where a bar's length has nothing to be measured against. The
 * reading is also stated for assistive tech, which cannot count spans.
 */
function FactorMeter({ score }: { score: number }) {
  const active = getActiveSegmentsFromScore(score > 1 ? score : score * 100)
  const color = getColorForEnergyMode(getEnergyMode(score > 1 ? score : score * 100))

  return (
    <span role="img" aria-label={`${score}/100`} style={{ display: 'inline-flex', gap: 1.5, flexShrink: 0 }}>
      {Array.from({ length: FACTOR_SEGMENTS }, (_, index) => (
        <span
          key={index}
          style={{
            width: 4,
            height: 11,
            borderRadius: 1,
            background: index < active ? color : '#4a4a4a',
          }}
        />
      ))}
    </span>
  )
}

export default EnergyDetailsCard
